import { useState } from 'react'
import { CheckCircle2, Copy, CreditCard, Loader2, Wallet } from 'lucide-react'
import {
  useSellablePlans,
  useMyPayments,
  useCheckoutBizum,
  useCheckoutStripe,
  useReportPayment,
} from '@/hooks/useBilling'
import { SkeletonCard } from '@/components/LoadingSkeleton'
import { StatusBadge } from '@/components/StatusBadge'
import { cn } from '@/lib/utils'
import { openExternalLink } from '@/lib/telegram'
import type { BizumCheckoutResponse, Payment, PaymentStatus } from '@/types/billing'

// P0.53 (29/08/2026, Sección 6 del encargo): ELITE se retira de este mapeo -- `GET
// /api/billing/plans` (fuente real de esta pantalla) nunca lo devuelve, así que la entrada
// nunca se alcanzaba desde aquí. Ningún plan vendible del Client Web queda fuera de FREE/PRO/
// BLACK.
const PLAN_LABEL: Record<string, string> = { PRO: 'Pro', BLACK: 'Black' }

const PAYMENT_STATUS_LABEL: Record<PaymentStatus, string> = {
  pending: 'Pendiente de pago',
  reported: 'En revisión',
  approved: 'Aprobado',
  paid: 'Pagado',
  rejected: 'Rechazado',
  expired: 'Vencido',
  cancelled: 'Cancelado',
  refunded: 'Reembolsado',
}

const PAYMENT_STATUS_TONE: Record<PaymentStatus, 'neutral' | 'success' | 'danger' | 'warn'> = {
  pending: 'warn',
  reported: 'warn',
  approved: 'success',
  paid: 'success',
  rejected: 'danger',
  expired: 'neutral',
  cancelled: 'neutral',
  refunded: 'neutral',
}

function PendingBizumCard({ checkout, payment }: { checkout: BizumCheckoutResponse | null; payment?: Payment }) {
  const reportPayment = useReportPayment()
  const [copied, setCopied] = useState(false)
  const reference = checkout?.reference ?? payment?.reference ?? ''
  const amount = checkout?.amount ?? payment?.amount ?? 0
  const phoneNumber = checkout?.phone_number

  return (
    <div className="card space-y-3 animate-fade-in-up motion-reduce:animate-none">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-accent-soft flex items-center justify-center">
          <CreditCard className="h-4 w-4 text-accent" />
        </div>
        <p className="text-sm font-semibold">Pago por Bizum</p>
      </div>

      {phoneNumber && (
        <div className="space-y-1.5 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-text-dim">Envía a</span>
            <span className="font-semibold tabular-nums">{phoneNumber}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-dim">Importe</span>
            <span className="font-semibold tabular-nums">{amount.toFixed(2)} €</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-text-dim shrink-0">Concepto (obligatorio)</span>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(reference)
                setCopied(true)
                setTimeout(() => setCopied(false), 1500)
              }}
              className="flex items-center gap-1 font-mono font-semibold text-accent"
            >
              {reference}
              {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      )}

      {payment && (
        <div className="flex items-center justify-between border-t border-border pt-2.5">
          <span className="text-xs text-text-dim">Estado</span>
          <StatusBadge tone={PAYMENT_STATUS_TONE[payment.status]}>
            {PAYMENT_STATUS_LABEL[payment.status]}
          </StatusBadge>
        </div>
      )}

      {payment && payment.status === 'pending' && (
        <button
          onClick={() => reportPayment.mutate(payment.id)}
          disabled={reportPayment.isPending}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-accent text-white text-sm font-medium disabled:opacity-50"
        >
          {reportPayment.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          He realizado el pago
        </button>
      )}

      {payment && payment.status === 'reported' && (
        <p className="text-xs text-text-muted leading-relaxed">
          Avisamos al administrador. Tu plan se activará en cuanto confirme el pago — recibirás
          un mensaje en Telegram.
        </p>
      )}
    </div>
  )
}

export function RenewPlanPage() {
  const { data: plansData, isLoading: plansLoading } = useSellablePlans()
  const { data: paymentsData, isLoading: paymentsLoading } = useMyPayments(true)
  const checkoutBizum = useCheckoutBizum()
  const checkoutStripe = useCheckoutStripe()
  const [localCheckout, setLocalCheckout] = useState<BizumCheckoutResponse | null>(null)

  const openPayment = paymentsData?.payments.find((p) => p.status === 'pending' || p.status === 'reported')

  if (plansLoading || paymentsLoading) {
    return (
      <div className="p-4 space-y-4">
        <h1 className="text-lg font-bold">Renovar plan</h1>
        <SkeletonCard />
      </div>
    )
  }

  if (localCheckout || openPayment) {
    return (
      <div className="p-4 space-y-4">
        <h1 className="text-lg font-bold">Renovar plan</h1>
        <PendingBizumCard checkout={localCheckout} payment={openPayment ?? localCheckout?.payment} />
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-lg font-bold">Renovar plan</h1>
      <p className="text-xs text-text-muted">
        Elige un plan y un método de pago. Con tarjeta el plan se activa al instante; por Bizum
        lo confirma el administrador.
      </p>

      <div className="space-y-3">
        {Object.entries(plansData?.plans ?? {}).map(([plan, price]) => (
          <div
            key={plan}
            className={cn('card space-y-2.5', plan === 'BLACK' && 'border-brand/50')}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">{PLAN_LABEL[plan] ?? plan}</p>
                {plan === 'BLACK' && (
                  <p className="text-[10px] text-brand font-semibold uppercase">Más completo</p>
                )}
              </div>
              <p className="text-base font-bold tabular-nums">{price.toFixed(2)} €/mes</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() =>
                  checkoutStripe.mutate(
                    { plan },
                    { onSuccess: (data) => openExternalLink(data.checkout_url) }
                  )
                }
                disabled={checkoutStripe.isPending}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-brand text-black text-xs font-semibold disabled:opacity-50"
              >
                {checkoutStripe.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CreditCard className="h-3.5 w-3.5" />
                )}
                Pagar con tarjeta
              </button>
              <button
                onClick={() => checkoutBizum.mutate({ plan }, { onSuccess: (data) => setLocalCheckout(data) })}
                disabled={checkoutBizum.isPending}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-panel-elevated text-text-muted text-xs font-medium disabled:opacity-50"
              >
                <Wallet className="h-3.5 w-3.5" />
                Bizum
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
